"use client"

import { useTranslations } from "next-intl"
import React, { useState, useEffect, useRef } from "react"
import { motion } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Truck, Plus } from "lucide-react"
import { ShippingOptionItem } from "@/components/ui/shipping-option"
import { ShippingMethodDialog } from "@/components/ui/shipping-method-dialog"
import { DeleteShippingMethodDialog } from "@/components/ui/delete-shipping-method-dialog"
import { v4 as uuidv4 } from "uuid"
import { PAYPAL_SUPPORTED_COUNTRIES } from "@/lib/paypal-countries"
import { DEFAULT_CURRENCY } from "@/lib/currencies"
import { creationFlowButtonOutlineClass, creationFlowBlockClass, creationFlowSectionLabelClass } from "@/lib/creation-flow-form-styles"
import { cn } from "@/lib/utils"
import { useToast } from "@/hooks/use-toast"
import {
  addShippingMethod,
  updateShippingMethod as updateShippingMethodApi,
  deleteShippingMethod as deleteShippingMethodApi,
  listShippingMethods,
  ShippingMethodError,
  type ShippingMethodRow,
} from "@/lib/listings/shipping-methods-actions"
import {
  createSellerShippingMethod,
  updateSellerShippingMethod,
  deleteSellerShippingMethod,
} from "@/lib/admin/shipping-methods"
import { createClient } from "@/utils/supabase/client"
import {
  ShippingModeToggleAndParcelForm,
  type ShippingModeAndParcelValue,
} from "@/components/shipping/ShippingModeToggleAndParcelForm"
import { type SupportTier } from "@/lib/shipping/origin-support"
import { getEffectiveTier } from "@/lib/shipping/country-access"
import { isCarrierAvailable } from "@/lib/shipping/carrier-eligibility"
import { useShippoBetaAccess } from "@/hooks/use-shippo-beta-access"
import { ShipFromAddressPicker } from "@/components/shipping/ship-from-address-picker"
import type { ShippingOriginBinding } from "@/lib/shipping/origin-binding"

function unreachable(x: never): never {
  throw new Error(`Unreachable ShippingOriginBinding kind: ${JSON.stringify(x)}`)
}

export type ShippingOption = {
  id: string
  name: string
  description: string
  icon: React.ElementType
  price: string
  bannedCountries: string[]
  allowCombined?: boolean
  combinedFee?: string
}

export type NewShippingMethod = {
  name: string
  description: string
  price: string
  bannedCountries: string[]
}

export class EditShippingDialogErrorBoundary extends React.Component<
  { children: React.ReactNode; dialogOpen: boolean; onOpenChange: (open: boolean) => void },
  { hasError: boolean; error: Error | null }
> {
  state = { hasError: false, error: null as Error | null }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidUpdate(prevProps: { dialogOpen: boolean }) {
    if (prevProps.dialogOpen && !this.props.dialogOpen && this.state.hasError) {
      this.setState({ hasError: false, error: null })
    }
  }

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          <p className="font-medium">Edit shipping method failed</p>
          <p className="mt-1 text-muted-foreground">Check the browser console for details.</p>
          <button
            type="button"
            className="mt-2 text-xs underline"
            onClick={() => {
              this.props.onOpenChange(false)
              this.setState({ hasError: false, error: null })
            }}
          >
            Close
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

interface EditShippingTabProps {
  initialSelectedMethods: string[]
  initialCustomOptions: any[]
  onUpdateAction: (selectedMethods: string[], customOptions: ShippingOption[]) => void
  /**
   * Resolved ship-from origin. Required — its `kind` decides whether the
   * embedded address picker renders, and its `country` feeds the
   * currency-mismatch explainer + `ShippingMethodDialog.baseCountry`.
   * Caller produces it via `useShippingOrigin` (mainstream surfaces) or
   * constructs `{ kind: 'fixed', country }` directly (chat-private etc.).
   * @see lib/shipping/origin-binding.ts
   */
  origin: ShippingOriginBinding
  currencyCode?: string
  compact?: boolean
  /**
   * Shippo white-label (WS3.2 — edit-flow parity). When provided, the
   * `ShippingModeToggleAndParcelForm` Primitive is mounted as a sibling
   * block above the legacy methods list. Omit on chat-private listings
   * where flat-rate is hard-coded.
   */
  shippingMode?: ShippingModeAndParcelValue
  onShippingModeChange?: (next: ShippingModeAndParcelValue) => void
  /**
   * When the listing has a `seller_orders` row with a locked in-flight
   * `external_rate_id`, the seller cannot retroactively flip
   * legacy ↔ live_rate. Greys the Primitive and shows
   * `shippingModeLockedReason`. WS3.2.b.
   */
  shippingModeLocked?: boolean
  shippingModeLockedReason?: string
  /**
   * When an admin edits another user's listing, this is the listing OWNER's
   * `user_id`. The tab then (1) reads that seller's methods (public-read RLS
   * allows it) and (2) routes every write through the admin service-role route
   * instead of the browser client (which can't write another user's methods).
   * Undefined for the normal seller-editing-own flow.
   */
  sellerUserId?: string
}

export function EditShippingTab({
  initialSelectedMethods,
  initialCustomOptions,
  onUpdateAction,
  origin,
  currencyCode = DEFAULT_CURRENCY,
  compact = false,
  shippingMode,
  onShippingModeChange,
  shippingModeLocked = false,
  shippingModeLockedReason,
  sellerUserId,
}: EditShippingTabProps) {
  // États internes pour la sélection et la gestion des options de shipping
  const tLC = useTranslations("listingCreation")
  const { toast } = useToast()
  const [selectedOptions, setSelectedOptions] = useState<string[]>(initialSelectedMethods)
  const [customShippingOptions, setCustomShippingOptions] = useState<ShippingOption[]>([])
  const [isLoadingOptions, setIsLoadingOptions] = useState(true) // État de chargement
  const [dialogOpen, setDialogOpen] = useState(false)
  const [newShippingMethod, setNewShippingMethod] = useState<NewShippingMethod>({
    name: "",
    description: "",
    price: "",
    bannedCountries: [],
  })
  const [shippingMethodError, setShippingMethodError] = useState("")
  const [isEditing, setIsEditing] = useState(false)
  const [editingId, setEditingId] = useState<string | undefined>()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deletingId, setDeletingId] = useState<string | undefined>()

  // Référence pour éviter de re-fusionner plusieurs fois
  const didSetMerged = useRef(false)

  // Origin country comes from the binding (single resolver upstream — see
  // hooks/use-shipping-origin.ts). Was three optional props that the
  // edit-listing-dialog silently forgot to pass; the `kind` discriminator
  // now forces every callsite to think about the picker explicitly.
  const dataCountryCode = origin.country
  // Per-seller beta enrolment for live-rate in non-GA countries (GB/DE/FR).
  // Fail-closed during load — `getEffectiveTier` collapses `beta`-tier
  // origins to "unsupported" when the flag is false, so the UI never
  // promises live-rate the server would reject.
  const { hasBetaAccess: hasShippoBetaAccess } = useShippoBetaAccess()
  const originTier: SupportTier =
    getEffectiveTier(dataCountryCode, hasShippoBetaAccess) ?? "unsupported"
  const originCountryLabel =
    PAYPAL_SUPPORTED_COUNTRIES.find((c) => c.code === dataCountryCode)?.name ??
    dataCountryCode

  // Carrier currency-mismatch gate (ADR_CURRENCY_PIN, 2026-06-23). Profile
  // currency = `currencyCode`; ship-from country = `dataCountryCode`. The
  // "Buffon case" (USD store shipping from ES) → disable live-rate + explain.
  const currencyMismatchExplainer: string | null = (() => {
    const e = isCarrierAvailable(
      { currency: currencyCode, shipFromCountry: dataCountryCode },
      "shippo",
      { hasBetaAccess: hasShippoBetaAccess },
    )
    if (e.ok || e.reason !== "CURRENCY_MISMATCH") return null
    return (
      `Your store is in ${currencyCode} but you ship from ${originCountryLabel} — ` +
      `live-rate shipping is unavailable. Either update your shipping ` +
      `origin to a US address or use flat-rate methods.`
    )
  })()

  // On récupère les shipping methods depuis Supabase et on fusionne avec la config existante,
  // mais uniquement lors du premier montage ou si le listing change.
  useEffect(() => {
    // Si on a déjà initialisé les options, on ne fait rien pour ne pas écraser les modifications de l’utilisateur.
    if (didSetMerged.current) return
    let loadingTimer: ReturnType<typeof setTimeout> | null = null

    async function fetchShippingMethods() {
      try {
        // Self-edit goes through the canonical builder (parses boundary +
        // throws ShippingMethodError on failure). Admin edit reads the
        // target seller's rows directly — public-read RLS on
        // `shipping_methods` allows it from the browser client and the
        // canonical builder does not yet take a foreign user_id parameter.
        let methodsData: ShippingMethodRow[]
        if (sellerUserId) {
          const supabase = createClient()
          const { data, error } = await supabase
            .from("shipping_methods")
            .select("*")
            .eq("user_id", sellerUserId)
          if (error) throw error
          methodsData = (data ?? []) as ShippingMethodRow[]
        } else {
          methodsData = await listShippingMethods()
        }
        const fetchedOptions: ShippingOption[] = methodsData.map((m) => ({
          id: m.id,
          name: m.name ?? "",
          description: m.description ?? "",
          icon: Truck,
          price: m.price ?? "0",
          bannedCountries: Array.isArray(m.banned_countries) ? m.banned_countries : [],
          allowCombined: false,
          combinedFee: "0",
        }))

        // Fusionner avec la configuration déjà présente (shipping_config)
        const mergedOptions = fetchedOptions.map((option) => {
          const parentOption = initialCustomOptions.find((item: any) => {
            // On compare l'id du shipping method avec shipping_method_id du listing
            return item.shipping_method_id === option.id
          })
          if (parentOption) {
            return {
              ...option,
              // Conversion explicite en booléen (au cas où la valeur soit une string "true")
              allowCombined:
                parentOption.allow_combined === true ||
                parentOption.allow_combined === "true" ||
                false,
              combinedFee: parentOption.combined_fee || "0",
              price: parentOption.price || option.price,
            }
          }
          return option
        })

        setCustomShippingOptions(mergedOptions)
        const validIds = mergedOptions.map((opt) => opt.id)
        const newSelected = initialSelectedMethods.filter((id) => validIds.includes(id))
        setSelectedOptions(newSelected)

        // Ajouter un petit délai avant de marquer comme chargé pour éviter le flash
        loadingTimer = setTimeout(() => {
          setIsLoadingOptions(false)
        }, 100)
      } catch (err) {
        console.error("Error fetching shipping methods", err)
        setCustomShippingOptions([])
        setSelectedOptions(initialSelectedMethods.filter((id) => typeof id === "string" && id.length > 0))
        loadingTimer = setTimeout(() => setIsLoadingOptions(false), 100)
      }
    }
    fetchShippingMethods()
    didSetMerged.current = true
    return () => {
      if (loadingTimer) clearTimeout(loadingTimer)
    }
  }, [initialCustomOptions, initialSelectedMethods, sellerUserId])

  // On informe le composant parent dès qu'il y a un changement dans la sélection ou la configuration
  useEffect(() => {
    // Only notify parent after initial merge is complete
    if (didSetMerged.current) {
      onUpdateAction(selectedOptions, customShippingOptions)
    }
  }, [selectedOptions, customShippingOptions, onUpdateAction])

  const toggleOption = (id: string) => {
    setSelectedOptions((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    )
    if (navigator.vibrate) navigator.vibrate(10)
  }

  const toggleCombinedShipping = (id: string) => {
    setCustomShippingOptions((prev) =>
      prev.map((option) =>
        option.id === id ? { ...option, allowCombined: !option.allowCombined } : option
      )
    )
  }

  const updateCombinedFee = (id: string, fee: string) => {
    const updatedFee = fee === "" ? "0" : fee
    setCustomShippingOptions((prev) =>
      prev.map((option) => (option.id === id ? { ...option, combinedFee: updatedFee } : option))
    )
  }

  const handleAddShippingMethod = async () => {
    if (!newShippingMethod.name.trim() || !newShippingMethod.price.trim()) return
    if (newShippingMethod.bannedCountries.length === PAYPAL_SUPPORTED_COUNTRIES.length) {
      setShippingMethodError("You must allow at least one country for shipping.")
      return
    }
    const newId = uuidv4()
    const newOption: ShippingOption = {
      id: newId,
      name: newShippingMethod.name,
      description: newShippingMethod.description || "",
      icon: Truck,
      price: newShippingMethod.price,
      bannedCountries: newShippingMethod.bannedCountries,
      allowCombined: false,
      combinedFee: "0",
    }
    
    // Mettre à jour les états
    const updatedOptions = [...customShippingOptions, newOption]
    const updatedSelected = [...selectedOptions, newId]
    
    setCustomShippingOptions(updatedOptions)
    setSelectedOptions(updatedSelected)
    
    // Notifier immédiatement le parent avec les nouveaux états pour s'assurer que la nouvelle méthode est sauvegardée
    onUpdateAction(updatedSelected, updatedOptions)

    try {
      if (sellerUserId) {
        await createSellerShippingMethod(sellerUserId, newOption.id, {
          name: newOption.name,
          description: newOption.description,
          price: newOption.price,
          bannedCountries: newOption.bannedCountries,
          currency: currencyCode,
        })
      } else {
        await addShippingMethod({
          id: newOption.id,
          name: newOption.name,
          description: newOption.description,
          price: newOption.price,
          banned_countries: newOption.bannedCountries,
          currency: currencyCode,
        })
      }
    } catch (err) {
      const description = err instanceof ShippingMethodError
        ? (err.serverError ?? "Failed to add shipping method.")
        : err instanceof Error
        ? err.message
        : "Failed to add shipping method."
      toast({ title: "Error", description, variant: "destructive" })
    }
    resetForm()
    if (navigator.vibrate) navigator.vibrate([10, 20, 10])
  }

  const handleEditShippingMethod = (id: string) => {
    const option = customShippingOptions.find((opt) => opt.id === id)
    if (!option) return
    setNewShippingMethod({
      name: String(option.name ?? ""),
      description: String(option.description ?? ""),
      price: String(option.price ?? ""),
      bannedCountries: Array.isArray(option.bannedCountries) ? option.bannedCountries : [],
    })
    setIsEditing(true)
    setEditingId(id)
    setDialogOpen(true)
  }

  const handleUpdateShippingMethod = async (id: string) => {
    if (!newShippingMethod.name.trim() || !newShippingMethod.price.trim()) return
    if (newShippingMethod.bannedCountries.length === PAYPAL_SUPPORTED_COUNTRIES.length) {
      setShippingMethodError("You must allow at least one country for shipping.")
      return
    }
    const updatedOption: ShippingOption = {
      id,
      name: newShippingMethod.name,
      description: newShippingMethod.description,
      icon: Truck,
      price: newShippingMethod.price,
      bannedCountries: newShippingMethod.bannedCountries,
      allowCombined: false,
      combinedFee: "0",
    }
    setCustomShippingOptions((prev) =>
      prev.map((option) => (option.id === id ? updatedOption : option))
    )
    try {
      if (sellerUserId) {
        await updateSellerShippingMethod(sellerUserId, id, {
          name: updatedOption.name,
          description: updatedOption.description,
          price: updatedOption.price,
          bannedCountries: updatedOption.bannedCountries,
          currency: currencyCode,
        })
      } else {
        await updateShippingMethodApi(id, {
          name: updatedOption.name,
          description: updatedOption.description,
          price: updatedOption.price,
          banned_countries: updatedOption.bannedCountries,
          currency: currencyCode,
        })
      }
    } catch (err) {
      const description = err instanceof ShippingMethodError
        ? (err.serverError ?? "Failed to update shipping method.")
        : err instanceof Error
        ? err.message
        : "Failed to update shipping method."
      toast({ title: "Error", description, variant: "destructive" })
    }
    resetForm()
    if (navigator.vibrate) navigator.vibrate([10, 20, 10])
  }

  const handleDeleteShippingMethod = (id: string) => {
    setDeletingId(id)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!deletingId) return
    setCustomShippingOptions((prev) => prev.filter((option) => option.id !== deletingId))
    setSelectedOptions((prev) => prev.filter((id) => id !== deletingId))
    setDeleteDialogOpen(false)
    try {
      if (sellerUserId) {
        await deleteSellerShippingMethod(sellerUserId, deletingId)
      } else {
        await deleteShippingMethodApi(deletingId)
      }
    } catch (err) {
      const description = err instanceof ShippingMethodError
        ? (err.serverError ?? "Failed to delete shipping method.")
        : err instanceof Error
        ? err.message
        : "Failed to delete shipping method."
      toast({ title: "Error", description, variant: "destructive" })
    }
    setDeletingId(undefined)
    if (navigator.vibrate) navigator.vibrate([10, 20, 10])
  }

  const resetForm = () => {
    setNewShippingMethod({ name: "", description: "", price: "", bannedCountries: [] })
    setShippingMethodError("")
    setDialogOpen(false)
    setIsEditing(false)
    setEditingId(undefined)
  }

  const sectionLabel = creationFlowSectionLabelClass
  const cardRadius = "rounded-3xl"
  const emptyCardBase = creationFlowBlockClass

  return (
    <div className={compact ? "space-y-4 px-1" : "space-y-6 px-2"}>
      {/* Shippo live-rate UI is behind the per-seller `beta_features` flag */}
      {/* (2026-06-28 — US/GB/DE/FR all beta). Without it, the seller edits */}
      {/* only the flat-rate list below — same as pre-Shippo. The flag is */}
      {/* fetched via `useShippoBetaAccess`; on dev set `BETA_FEATURES_BYPASS` */}
      {/* in .env.local to grant it for every seller without a DB INSERT. */}
      {hasShippoBetaAccess && shippingMode && onShippingModeChange ? (
        <ShippingModeToggleAndParcelForm
          value={shippingMode}
          onChange={onShippingModeChange}
          sellerCountry={dataCountryCode}
          compact={compact}
          disabled={shippingModeLocked}
          disabledReason={shippingModeLockedReason}
          originTier={originTier}
          originCountryLabel={originCountryLabel}
          currencyMismatchExplainer={currencyMismatchExplainer}
        />
      ) : null}
      {(() => {
        // Exhaustive switch on origin.kind — adding a third kind to
        // ShippingOriginBinding will red-compile here and force the new
        // case to be handled explicitly (no silent fallthrough).
        switch (origin.kind) {
          case "editable":
            return (
              <ShipFromAddressPicker
                value={origin.address.value}
                onChange={origin.address.onChange}
                compact={compact}
              />
            )
          case "fixed":
            return null
          default:
            return unreachable(origin)
        }
      })()}
      <motion.div className="space-y-4" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <p className={cn(sectionLabel, "px-0.5")}>{tLC("shippingMethods")}</p>
        {isLoadingOptions ? (
          <div className={cn("flex h-32 items-center justify-center", cardRadius, emptyCardBase, "p-4")}>
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-sheet-handle border-t-muted-foreground" />
          </div>
        ) : (
          <>
            {customShippingOptions.length === 0 ? (
              <div className={cn("text-center py-10 px-4", cardRadius, emptyCardBase)}>
                <Truck className="w-12 h-12 mx-auto mb-4 text-muted-foreground/70" />
                <p className="font-medium mb-1 text-foreground">No shipping methods yet</p>
                <p className="text-sm text-muted-foreground">Add your first shipping method to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {customShippingOptions.map((option, index) => (
                  <motion.div key={option.id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
                    <ShippingOptionItem
                      option={option}
                      currencyCode={currencyCode}
                      isSelected={selectedOptions.includes(option.id)}
                      onToggle={toggleOption}
                      onToggleCombined={toggleCombinedShipping}
                      onUpdateCombinedFee={updateCombinedFee}
                      onEdit={handleEditShippingMethod}
                      onDelete={handleDeleteShippingMethod}
                      variant="dark"
                    />
                  </motion.div>
                ))}
              </div>
            )}

            <EditShippingDialogErrorBoundary
              dialogOpen={dialogOpen}
              onOpenChange={(open) => {
                setDialogOpen(open)
                if (!open) resetForm()
              }}
            >
              <ShippingMethodDialog
                open={dialogOpen}
                onOpenChangeAction={(open) => {
                  setDialogOpen(open)
                  if (!open) resetForm()
                }}
                newShippingMethod={newShippingMethod}
                setNewShippingMethodAction={setNewShippingMethod}
                onAddMethodAction={handleAddShippingMethod}
                onUpdateMethod={handleUpdateShippingMethod}
                error={shippingMethodError}
                baseCountry={dataCountryCode}
                isEditing={isEditing}
                editingId={editingId}
                currencyCode={currencyCode}
              />
            </EditShippingDialogErrorBoundary>

            <Button
              variant="outline"
              className={cn(
                compact ? "w-full py-4 text-sm" : "w-full py-5 text-base",
                "flex items-center justify-center gap-2 rounded-2xl border-dashed",
                creationFlowButtonOutlineClass
              )}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setDialogOpen(true)
              }}
            >
              <Plus className="w-5 h-5" />
              Add Shipping Method
            </Button>
          </>
        )}
      </motion.div>

      <DeleteShippingMethodDialog
        open={deleteDialogOpen}
        onOpenChangeAction={setDeleteDialogOpen}
        shippingMethodName={
          deletingId
            ? customShippingOptions.find((option) => option.id === deletingId)?.name || "Unknown"
            : ""
        }
        onConfirmAction={confirmDelete}
      />
    </div>
  )
}
